import React from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, theme, useResponsive } from '@sg/garnish';

import { type useOrganizationLinksFooterContentfulData } from '../../../hooks/useOrganizationLinksContentfulData';
import { OrganizationLink } from '../../OrganizationLink';

// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationLinksFooterSection = (props: Props) => {
  const { section, conditionalLinksRules, palette } = props;

  const { match } = useResponsive();

  const textStyle = palette === 'dark' ? styles.lightText : styles.darkText;

  return (
    <View style={match([styles.xsMargin, undefined])}>
      <BodyText style={[styles.title, textStyle]}>{section.name}</BodyText>

      {section.links.map((link) => {
        const Link = getOrganizationLink(link);

        return <Link key={link.label} link={link} textStyle={textStyle} />;
      })}

      {/* Links that will be displayed only if the specified conditions are met. */}
      {section.conditionalLinks?.map((link) => {
        const Link = getOrganizationLink(link);
        const canBeRendered = link.id ? conditionalLinksRules?.[link.id] === true : false; // prettier-ignore

        return canBeRendered ? <Link key={link.label} link={link} textStyle={textStyle} /> : null;
      })}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getOrganizationLink(link: Props['section']['links'][0]) {
  if (link.id === LINKS_IDS.PRIVACY_CHOICES) {
    return OrganizationLink.PrivacyChoices;
  }

  if (link.id === LINKS_IDS.CONTACT_US) {
    return OrganizationLink.ContactUs;
  }

  return OrganizationLink.Base;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LINKS_IDS = {
  PRIVACY_CHOICES: 'footer-privacy-choices',
  CONTACT_US: 'footer-contact-us',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  darkText: {
    color: theme.colors.DARK_KALE,
  },
  lightText: {
    color: theme.colors.WHITE,
  },
  title: {
    marginBottom: theme.spacing['4'],
    textTransform: 'uppercase',
  },
  xsMargin: {
    marginBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  section: ReturnType<typeof useOrganizationLinksFooterContentfulData>['sections'][0];
  conditionalLinksRules?: Record<string, boolean>;
  palette: 'light' | 'dark';
};
